// src/components/dashboard/ManagedCoursesWidget.jsx
// Dashboard widget showing courses/electives the user has been granted permissions to manage.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiShield, FiCalendar, FiFileText, FiVideo, FiImage, FiBookOpen, FiBell } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

// Permission keys → metadata + content management tab on CourseDetail
const PERMISSION_META = {
  'course_content:announcements:manage': { label: 'Announcements', icon: <FiBell size={11} />, tab: 'announcements' },
  'course_content:syllabus:manage': { label: 'Syllabus', icon: <FiFileText size={11} />, tab: 'syllabus' },
  'course_content:slides:manage': { label: 'Slides', icon: <FiImage size={11} />, tab: 'slides' },
  'course_content:readings:manage': { label: 'Readings', icon: <FiBookOpen size={11} />, tab: 'readings' },
  'course_content:recordings:manage': { label: 'Recordings', icon: <FiVideo size={11} />, tab: 'recordings' },
  'course_content:notifications:manage': { label: 'Notifications', icon: <FiBell size={11} />, tab: 'notifications' },
  'course_attendance:manage': { label: 'Attendance', icon: <FiCalendar size={11} />, tab: null, isAttendance: true },
};

const Container = styled(motion.section)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 -1.5rem 1.5rem;
  padding: 0 1.5rem 1.25rem;
  border-bottom: 1px solid var(--border-color);
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--dashboard-text);
  margin: 0;
  line-height: 1.4;
  letter-spacing: normal;
`;

const Meta = styled.div`
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.875rem;
`;

const CourseCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-primary);
    border-color: var(--primary-color);
  }
`;

const CardClickArea = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font-family: inherit;
  width: 100%;
`;

const CardAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 6px;
  background: var(--primary-color);
  color: white;
  font-weight: 700;
  font-size: 0.9375rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`;

const CourseTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
`;

const CourseName = styled.span`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CourseCode = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const PrimaryActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.625rem 0.75rem;
  background: transparent;
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  font-size: 0.8125rem;
  color: var(--primary-color);
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  letter-spacing: -0.005em;

  &:hover {
    background: var(--primary-color);
    border-color: var(--primary-color);
    color: white;
  }
`;

const PermissionsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
`;

const PermissionChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.3125rem 0.5625rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(184, 149, 106, 0.1);
    color: var(--primary-color);
    border-color: rgba(184, 149, 106, 0.4);
  }
`;

function ManagedCoursesWidget() {
  const navigate = useNavigate();
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await apiService.request('/permissions/my-granted-scopes');
        if (data.success) {
          setScopes(data.scopes || []);
        }
      } catch (error) {
        logger.error('Error fetching managed courses', { error });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading || scopes.length === 0) return null;

  const courseBaseUrl = (scope) => scope.scope_type === 'course'
    ? `/courses/${scope.scope_id}`
    : `/electives/${scope.scope_id}`;

  const permissionUrl = (scope, permissionKey) => {
    const meta = PERMISSION_META[permissionKey];
    if (!meta) return courseBaseUrl(scope);
    if (meta.isAttendance) {
      const param = scope.scope_type === 'course' ? 'courseId' : 'electiveId';
      return `/admin/attendance?${param}=${scope.scope_id}`;
    }
    if (meta.tab) return `${courseBaseUrl(scope)}?manageContent=true&tab=${meta.tab}`;
    return courseBaseUrl(scope);
  };

  return (
    <Container
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
    >
      <Header>
        <Title>
          <FiShield size={20} />
          My Managed Courses
        </Title>
        <Meta>{scopes.length} {scopes.length === 1 ? 'Course' : 'Courses'}</Meta>
      </Header>

      <Grid>
        {scopes.map((scope) => (
          <CourseCard key={`${scope.scope_type}-${scope.scope_id}`}>
            <CardClickArea
              type="button"
              onClick={() => navigate(courseBaseUrl(scope))}
              aria-label={`Open ${scope.name}`}
            >
              <CardAvatar>{scope.name.charAt(0)}</CardAvatar>
              <CardHeader>
                <CourseTitleRow>
                  <CourseName>{scope.name}</CourseName>
                </CourseTitleRow>
                {scope.code && (
                  <CourseCode>{scope.code}</CourseCode>
                )}
              </CardHeader>
            </CardClickArea>
            {(() => {
              const PRIMARY_KEYS = ['course_content:recordings:manage', 'course_attendance:manage'];
              const primaryGranted = PRIMARY_KEYS.filter(k => scope.permissions.includes(k));
              const otherGranted = scope.permissions.filter(k => !PRIMARY_KEYS.includes(k));

              return (
                <>
                  {primaryGranted.length > 0 && (
                    <PrimaryActions>
                      {primaryGranted.map((key) => {
                        const meta = PERMISSION_META[key];
                        if (!meta) return null;
                        return (
                          <PrimaryButton
                            key={key}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(permissionUrl(scope, key));
                            }}
                          >
                            {React.cloneElement(meta.icon, { size: 13 })}
                            {meta.label}
                          </PrimaryButton>
                        );
                      })}
                    </PrimaryActions>
                  )}
                  {otherGranted.length > 0 && (
                    <PermissionsList>
                      {otherGranted.map((key) => {
                        const meta = PERMISSION_META[key];
                        if (!meta) return null;
                        return (
                          <PermissionChip
                            key={key}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(permissionUrl(scope, key));
                            }}
                            title={`Go to ${meta.label}`}
                          >
                            {meta.icon}
                            {meta.label}
                          </PermissionChip>
                        );
                      })}
                    </PermissionsList>
                  )}
                </>
              );
            })()}
          </CourseCard>
        ))}
      </Grid>
    </Container>
  );
}

export default ManagedCoursesWidget;
